import type { CampaignSchema, ContributionRateGroupSchema } from "@/types";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useCampaign } from "@/hooks/use-campaign";
import { useAppStore } from "@/stores";
import { CampaignNotice } from "./campaign-notice";
import { useCampaignTab } from "./campaign-shell/campaign-tab-context";
import { useTranslation } from "react-i18next";

/**
 * How a campaign's games count toward its turnover targets (#3783).
 *
 * The dashboard answers a different question from the in-game overlay, because it knows
 * something different. The overlay always has a game id and can say *"this game counts
 * 33%"*; the dashboard is routinely opened from the lobby, between games, so it has no
 * game to resolve against. It answers the question it can — **which games are worth
 * most** — and that is why this is a grouped list rather than a single figure.
 *
 * Grouped, never per game: a campaign's configured rows run to tens of thousands (the
 * backend materialises one per matched game), the same reason eligible games sit behind a
 * windowed dialog.
 */

/** The stored rate is 0..1; one decimal, because a player reading "12.35%" gains nothing
 *  from the second. Matches the overlay's own conversion exactly. */
const toPercent = (rate: number) => Math.round(rate * 1000) / 10;

const RateRow = ({ group }: { group: ContributionRateGroupSchema }) => {
  const { t } = useTranslation();
  const percent = toPercent(group.rate);
  const traits = group.traits ?? [];

  return (
    <li className="flex items-center gap-3 border-b border-foreground/[0.07] py-2.5 last:border-b-0">
      <span
        className={cn(
          "min-w-[46px] shrink-0 font-heading text-[13px] font-bold tabular-nums lg:text-[15px]",
          // The two ends of the range carry meaning the middle doesn't: full weight is the
          // good news, zero is the one a player has to act on. Neither is an error colour —
          // a game counting for nothing is a campaign configured as intended.
          percent >= 100 && "text-primary",
          percent === 0 && "text-muted-foreground"
        )}
      >
        {t("contributionRates.percent", "{{percent}}%", { percent })}
      </span>

      <span className="min-w-0 flex-1 font-body text-[12px] leading-4 text-foreground-secondary lg:text-[14px] lg:leading-[18px]">
        {traits.length > 0
          ? // The operator's own rule, in their words ("live", "netent") — the game groups a
            // player recognises. Not truncated: this is the half of the row that says which
            // games it is talking about.
            traits.join(", ")
          : // Set per game rather than by trait, so there is no category to name.
            t("contributionRates.games", "{{count}} games", {
              count: group.game_count,
            })}
      </span>

      {traits.length > 0 && (
        <span className="shrink-0 font-heading text-[11px] whitespace-nowrap text-muted-foreground tabular-nums">
          {t("contributionRates.games", "{{count}} games", {
            count: group.game_count,
          })}
        </span>
      )}
    </li>
  );
};

const RateList = ({ groups }: { groups: ContributionRateGroupSchema[] }) => {
  const { t } = useTranslation();

  return (
    <>
      <ul className="flex w-full flex-col">
        {groups.map((group, i) => (
          <RateRow key={`${group.rate}-${i}`} group={group} />
        ))}
      </ul>
      {/* Games with no configured rate never appear as a group — they count in full, and
          saying so once is honest where a "100% · everything else" row would imply the
          backend had enumerated the rest of the catalogue. */}
      <p className="mt-3 font-body text-[12px] leading-[18px] text-muted-foreground">
        {t("contributionRates.rest", "All other games count in full.")}
      </p>
    </>
  );
};

/**
 * The note under a turnover task's progress bar, pointing at the full breakdown.
 *
 * Where the player actually needs it: the breakdown under T&C answers "which games are
 * worth most", but nothing sends them there, and a bar moving slower than the wagers behind
 * it is the moment the question occurs. This is the one place the two connect — and the
 * reason the breakdown having a single home matters, since this link has to name it.
 *
 * Shown only when at least one group counts for **less than full** — a campaign whose
 * only configured rates are 100% has nothing to caveat, and "some games count less" would
 * be plainly false.
 *
 * The overlay knows the game it is docked beside (`gameFit`), so there it says what *this*
 * game counts, as the legacy overlay did — "This game counts 33%", "This game doesn't count" —
 * and nothing when the game counts in full.
 */
export const TurnoverContributionNote = () => {
  const { t } = useTranslation();
  const { campaignData } = useCampaign();
  const setTab = useCampaignTab();
  const gameRate = useAppStore((s) =>
    campaignData ? s.gameFit?.[campaignData.id]?.contributionRate : undefined
  );

  const reduced = (campaignData?.contribution_rates ?? []).some(
    (group) => group.rate < 1
  );
  // One decimal, as legacy: a rate is stored at 4dp, and "12.35%" tells a player nothing more.
  const gamePercent =
    gameRate === undefined ? null : Math.round(gameRate * 1000) / 10;
  if (gamePercent === null ? !reduced : gamePercent >= 100) return null;

  return (
    <CampaignNotice>
      {gamePercent === null
        ? t(
            "contributionRates.taskNote",
            "Some games count less toward this target."
          )
        : gamePercent === 0
          ? t("contributionRates.thisGameDoesNotCount")
          : t("contributionRates.thisGameCounts", { percent: gamePercent })}
      {/* No link in surfaces without tabs (the in-game button renders the same row), so
          the sentence still reads correctly on its own. */}
      {setTab && (
        <button
          type="button"
          onClick={() => setTab("terms")}
          className="cursor-pointer font-body text-[12px] text-primary underline underline-offset-[3px] transition-colors hover:text-primary/80 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
        >
          {t("contributionRates.seeHow", "See how games count")}
        </button>
      )}
    </CampaignNotice>
  );
};

/** T&C tab — the same data, rendered below the authored terms and visibly distinct from
 *  them. Generated from the campaign's live configuration on purpose: rates typed into the
 *  authored copy by hand drift from what processing applies the first time anyone retunes
 *  one, inside the text that is legally operative. */
export const CampaignContributionTerms = ({
  campaign,
}: {
  campaign: CampaignSchema;
}) => {
  const { t } = useTranslation();
  const groups = campaign.contribution_rates ?? [];

  if (groups.length === 0) return null;

  return (
    <Card
      title={t("contributionRates.termsHeading", "Game contribution")}
      className="text-left"
    >
      <RateList groups={groups} />
    </Card>
  );
};
